/** Parse one stock/search result entry (array or object shape). */
export function parseStockSearchEntry(info) {
  if (info == null) {
    return { companyName: "", sector: "", industry: "" };
  }

  if (Array.isArray(info)) {
    return {
      companyName: String(info[0] ?? "").trim(),
      sector: String(info[1] ?? "").trim(),
      industry: String(info[2] ?? "").trim(),
    };
  }

  if (typeof info === "object") {
    const companyName =
      info.companyName ?? info.company_name ?? info.name ?? info.company ?? "";
    const sector = info.sector ?? info.sectorName ?? info.sector_name ?? "";
    const industry = info.industry ?? info.industryName ?? info.industry_name ?? "";
    // Intentionally ignore any `keywords` field — stock search no longer returns them.
    return {
      companyName: String(companyName).trim(),
      sector: String(sector).trim(),
      industry: String(industry).trim(),
    };
  }

  if (typeof info === "string") {
    return { companyName: info.trim(), sector: "", industry: "" };
  }

  return { companyName: "", sector: "", industry: "" };
}

/** Build ticker dropdown options and per-ticker sector/industry metadata. */
export function buildTickerOptionsFromStockSearch(results) {
  if (!results || typeof results !== "object") {
    return { options: [], metadataByTicker: {} };
  }

  const metadataByTicker = {};
  const options = Object.entries(results).map(([ticker, info]) => {
    const { companyName, sector, industry } = parseStockSearchEntry(info);
    metadataByTicker[ticker] = { companyName, sector, industry };
    return {
      value: ticker,
      label: companyName ? `${ticker} - ${companyName}` : ticker,
    };
  });

  options.sort((a, b) => a.value.localeCompare(b.value));
  return { options, metadataByTicker };
}

export function deriveSectorIndustryFromTickers(tickers, metadataByTicker) {
  const sectors = new Set();
  const industries = new Set();

  for (const ticker of tickers || []) {
    const meta = metadataByTicker?.[ticker];
    if (meta?.sector) sectors.add(meta.sector);
    if (meta?.industry) industries.add(meta.industry);
  }

  return {
    sectors: [...sectors].sort((a, b) => a.localeCompare(b)),
    industries: [...industries].sort((a, b) => a.localeCompare(b)),
  };
}

/** Preserve manual sector/industry picks while applying ticker autofill. */
export function mergeTickerAutofillSectorIndustry(prev, prevTickers, nextTickers, metadataByTicker) {
  const prevDerived = deriveSectorIndustryFromTickers(prevTickers, metadataByTicker);
  const nextDerived = deriveSectorIndustryFromTickers(nextTickers, metadataByTicker);

  const manualSectors = (prev.sectors || []).filter((s) => !prevDerived.sectors.includes(s));
  const manualIndustries = (prev.industries || []).filter((i) => !prevDerived.industries.includes(i));

  return {
    sectors: [...new Set([...manualSectors, ...nextDerived.sectors])].sort((a, b) => a.localeCompare(b)),
    industries: [...new Set([...manualIndustries, ...nextDerived.industries])].sort((a, b) => a.localeCompare(b)),
  };
}
